import { DateTime } from "luxon";
import { z } from "zod";
import { zodToJsonSchema } from "zod-to-json-schema";

import { AppointmentCreatedBy } from "@/packages/shared/src/agent-api/requests";
import { AppointmentStatus, AppointmentType } from "@/packages/shared/src/models/appointment";
import { formatDateTimeDisplay } from "@/packages/shared/src/utils/date-utils";
import { getGeminiCompatibleSchema } from "@/src/lib/gemini-schema";
import { isoDateTimeSchema } from "@/src/models/common-schema";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { Group } from "@/src/models/group";
import { patientSchema } from "@/src/models/patient";
import type { Practitioner } from "@/src/models/practitioner";

export { AppointmentType };

export enum EhrAppointmentIdNamespace {
  ATHENA = "athena",
  ECW = "ecw",
}

export enum AppointmentIntegrationNamespace {
  COBALT = "cobalt",
}

const appointmentIdentifierSchema = z.object({
  namespace: z.nativeEnum(EhrAppointmentIdNamespace),
  id: z.string().min(1),
});

const appointmentIntegrationIdentifierSchema = z.object({
  namespace: z.nativeEnum(AppointmentIntegrationNamespace),
  id: z.string().min(1),
});

export const appointmentTargetSchema = z
  .object({
    version: z.literal(1),
    valerieAppointmentId: z.string().min(1).optional(),
    ehr: appointmentIdentifierSchema.nullable(),
    integration: appointmentIntegrationIdentifierSchema.nullable(),
    resolutionHints: z
      .object({
        appointmentDatetime: isoDateTimeSchema,
      })
      .optional(),
  })
  .refine((target) => target.ehr !== null || target.integration !== null, {
    message: "Appointment target requires an EHR or integration identifier",
  });

export type AppointmentTarget = z.infer<typeof appointmentTargetSchema>;

export const appointmentBasicSchema = z.object({
  appointmentCreatedAt: isoDateTimeSchema.nullable(),
  appointmentDatetime: isoDateTimeSchema.nullable(),
  createdBy: z.nativeEnum(AppointmentCreatedBy).nullable().optional(),
  patientId: z.string(),
  type: z.nativeEnum(AppointmentType),
  status: z.nativeEnum(AppointmentStatus).nullable(),
  ehrId: z.string().nullable(),
  ehrStatus: z.string().nullable(),
  ehrType: z.string().nullable(),
  ehrAppointmentTypeId: z.string().nullable(),
  ehrReason: z.string().nullable(),
  providerEhrId: z.string().nullable(),
  practitionerId: z.string().nullable(),
  durationMinutes: z.number().int().positive().nullish(),
});

export const appointmentSchema = z.object({
  ...appointmentBasicSchema.shape,
});

export const appointmentJsonSchema = zodToJsonSchema(appointmentSchema, {
  definitions: {
    patient: patientSchema,
  },
  $refStrategy: "none",
});

export const appointmentGeminiSchema = getGeminiCompatibleSchema(appointmentSchema, {
  patient: patientSchema,
});

export type Appointment = z.infer<typeof appointmentSchema> & {
  displayId: string | null;
  /** Backend-only identity; the API signs it before exposing an opaque action reference to callers. */
  appointmentTarget?: AppointmentTarget;
  /** Canonical namespace for `ehrId`; null on legacy rows until backfill establishes its meaning. */
  ehrIdNamespace: EhrAppointmentIdNamespace | null;
  /** Identifier assigned by an intermediary scheduling integration, separate from the source EHR ID. */
  integrationAppointmentId: string | null;
  /** Namespace for `integrationAppointmentId`; populated and cleared together with that identifier. */
  integrationNamespace: AppointmentIntegrationNamespace | null;
  /**
   * Source-EHR scheduling resource identifier. Cobalt exposes this separately from the rendering
   * provider as `secondary_provider_ehr_id`; direct-provider appointments commonly carry the same
   * identifier in both fields. Runtime-only because existing integrations and the appointment table
   * model only the rendering provider.
   */
  resourceEhrId?: string | null;
  /**
   * Source-EHR appointment identifier used for cross-adapter reads and parity comparisons. Some
   * adapters keep their own operational id in `ehrId` for writes, so this runtime-only field
   * preserves the underlying EHR identifier without changing persisted appointment semantics.
   */
  sourceEhrAppointmentId?: string | null;
} & DatabaseDefaults;

export type AppointmentWithPractitioner = Appointment & { practitioner: Practitioner | null };

/**
 * Appointment enriched with the EHR location identifier, produced by scheduling integrations
 * that expose location data on booked appointments (Athena departmentid, Cobalt facility id).
 * Not persisted to the DB — the extra field is a runtime-only decoration.
 */
export type BookedAppointmentWithLocation = Appointment & {
  locationEhrId?: string | null;
  start?: string;
  end?: string;
};

export type PartialAppointment = Partial<Appointment>;

export const appointmentShape: Appointment = {
  id: "",
  groupId: "",
  appointmentCreatedAt: null,
  appointmentDatetime: null,
  createdBy: null,
  type: AppointmentType.INITIAL_CONSULTATION,
  status: AppointmentStatus.SCHEDULED,
  ehrId: null,
  ehrIdNamespace: null,
  ehrStatus: null,
  ehrType: null,
  ehrAppointmentTypeId: null,
  ehrReason: null,
  integrationAppointmentId: null,
  integrationNamespace: null,
  patientId: "",
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  displayId: null,
  practitionerId: null,
  providerEhrId: null,
};

export const appointmentMetadataSchema = z.object({
  appointmentDatetime: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.nativeEnum(AppointmentCreatedBy).nullable(),
  displayId: z.string().nullable(),
  groupId: z.string().uuid(),
  id: z.string().uuid(),
  patientDisplayId: z.string().nullable(),
  patientFamilyName: z.string().nullable(),
  patientGivenName: z.string().nullable(),
  patientId: z.string().uuid(),
  status: z.nativeEnum(AppointmentStatus).nullable(),
  type: z.nativeEnum(AppointmentType),
  updatedAt: z.string().nullable(),
});

export type AppointmentMetadata = z.infer<typeof appointmentMetadataSchema>;

export const appointmentMetadataFiltersSchema = z.object({
  createdBy: z.string().optional(),
  page: z.coerce.number().default(0),
  pageSize: z.coerce.number().default(30),
  sortBy: z.string().default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
});

export type AppointmentMetadataFiltersParams = z.infer<typeof appointmentMetadataFiltersSchema>;

export interface PaginatedAppointmentsMetadataResponse {
  appointments: AppointmentMetadata[];
  totalCount: number;
}

type AppointmentLike = Pick<Appointment, "appointmentDatetime">;

/**
 * Re-express an appointment's datetime in a specific IANA timezone (same instant, different offset).
 * No-op when the appointment has no datetime or no timezone is given.
 */
export function applyTimezone<T extends AppointmentLike>(appointment: T, timezone: string | null | undefined): T {
  if (!appointment.appointmentDatetime || !timezone) {
    return appointment;
  }

  return {
    ...appointment,
    appointmentDatetime: DateTime.fromISO(appointment.appointmentDatetime).setZone(timezone).toISO(),
  };
}

export function applyGroupTimezone<T extends AppointmentLike>(appointment: T, group: Group): T {
  return applyTimezone(appointment, group.timezone);
}

export function schedulingLlmFields(appointment: AppointmentWithPractitioner, group: Group) {
  const withTimezone = applyGroupTimezone(appointment, group);

  if (withTimezone.appointmentDatetime == null) {
    throw new Error("Appointments must contain datetimes");
  }

  const providerName = [appointment.practitioner?.givenName, appointment.practitioner?.familyName]
    .filter(Boolean)
    .join(" ");

  return {
    providerNpi: appointment.practitioner?.npiNumber ?? "N/A",
    providerName: providerName || "N/A",
    dateTime: formatDateTimeDisplay(withTimezone.appointmentDatetime),
    appointmentReason: appointment.ehrReason,
    type: withTimezone.type,
    status: withTimezone.status,
  };
}

export { AppointmentCreatedBy };
